import { AsyncPipe, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AnalyticsService } from './services/analytics.service';
import { SessionService, isRejectedCredential } from './services/session.service';
import { AppUpdateService } from './services/app-update.service';
import { UserService } from './services/user.service';
import { DeviceService } from './services/device.service';
import { ThemeService } from './services/theme.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { injectQueryClient } from '@tanstack/angular-query-experimental';
import { PageTemplateComponent } from './page-template/page-template.component';
import { UiToastComponent } from './ui/toast/toast.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [PageTemplateComponent, AsyncPipe, RouterOutlet, UiToastComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  sessionService = inject(SessionService);
  private userService = inject(UserService);
  device = inject(DeviceService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly queryClient = injectQueryClient();
  readonly showTopBar = signal(false);
  private hasAuthenticatedSession = false;

  constructor() {
    // Construct ThemeService at bootstrap so its effect applies the resolved
    // class on the root element before the first route renders.
    inject(ThemeService);
    // Watches for new deploys from bootstrap on; the categories page shows the refresh prompt.
    inject(AppUpdateService);
  }

  ngOnInit() {
    this.syncShellClass();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncShellClass());

    this.sessionService.session$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(session => {
      if (session?.user) {
        this.hasAuthenticatedSession = true;
        this.analytics.setUser(session.user);
      } else if (this.hasAuthenticatedSession) {
        this.hasAuthenticatedSession = false;
        this.analytics.setUser(null);
      }
    });
    if (this.sessionService.hasPersistedSession) setTimeout(() => this.refreshPersistedSession());
  }

  private refreshPersistedSession(): void {
    // Restored query data cannot validate authentication after a reload.
    void this.queryClient
      .fetchQuery({ ...this.userService.refreshSessionQueryOptions(), staleTime: 0 })
      // Only a rejected credential ends the session. A timeout or dropped connection
      // (the refresh runs even while offline) must not sign out a valid user.
      .catch((error: unknown) => {
        if (isRejectedCredential(error)) this.sessionService.clearSession();
      });
  }

  private syncShellClass() {
    let activeRoute = this.route.snapshot;
    let hideTopBar = activeRoute.data['hideTopBar'] === true;
    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild;
      hideTopBar ||= activeRoute.data['hideTopBar'] === true;
    }

    this.showTopBar.set(!hideTopBar);
    this.document.body.classList.add('spartan-ui');
    this.document.body.classList.remove('legacy-ui');
  }
}
